/**
 * TypeScript Interfaces for Scene Image Generation Feature
 * Feature: Generate First Frames for Instagram Reels
 *
 * Clean architecture types for batch image generation,
 * individual scene regeneration, and progress tracking.
 */

import { Id } from '../../convex/_generated/dataModel';
import type { ImageGenerationStatus } from './reelScenes';

// ============================================================================
// Core Type Definitions
// ============================================================================

/**
 * Scene Image Generation Phase
 * Tracks the current state of batch image generation
 */
export type SceneImageGenerationPhase =
  | 'idle'
  | 'preparing'
  | 'generating'
  | 'completed'
  | 'partial_success'
  | 'failed';

/**
 * Individual Scene Generation Status
 * Tracks generation state for a single scene
 */
export type SingleSceneGenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';

// ============================================================================
// State Interfaces
// ============================================================================

/**
 * Scene Image Generation Progress
 * Tracks progress during batch generation
 */
export interface ISceneImageGenerationProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
}

/**
 * Individual Scene Result
 * Result of image generation for a single scene
 */
export interface ISceneGenerationResult {
  sceneId: Id<'reelScenes'>;
  sceneNumber: number;
  success: boolean;
  imageStorageId?: Id<'_storage'>;
  imageUrl?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Scene Image Generation State
 * Main state for the useSceneImageGeneration hook
 */
export interface ISceneImageGenerationState {
  phase: SceneImageGenerationPhase;
  progress: ISceneImageGenerationProgress;
  results: ISceneGenerationResult[];
  error: string | null;
  errorCode: string | null;
}

/**
 * Individual Scene Image State
 * State for single scene regeneration
 */
export interface ISingleSceneImageState {
  sceneId: Id<'reelScenes'> | null;
  frame: 'first' | 'last' | null;
  status: SingleSceneGenerationStatus;
  error: string | null;
  isEditingPrompt: boolean;
  editedPrompt: string;
}

// ============================================================================
// Action Types
// ============================================================================

/**
 * Scene Image Generation Action Types
 * Reducer actions for batch generation
 */
export type SceneImageGenerationAction =
  | { type: 'START_BATCH_GENERATION'; payload: { totalScenes: number } }
  | { type: 'SCENE_STARTED'; payload: { sceneId: Id<'reelScenes'>; sceneNumber: number } }
  | { type: 'SCENE_COMPLETED'; payload: ISceneGenerationResult }
  | { type: 'SCENE_FAILED'; payload: ISceneGenerationResult }
  | { type: 'BATCH_COMPLETED'; payload: { succeeded: number; failed: number } }
  | { type: 'BATCH_FAILED'; payload: { error: string; errorCode?: string } }
  | { type: 'RESET' };

/**
 * Single Scene Image Action Types
 * Reducer actions for individual scene regeneration
 */
export type SingleSceneImageAction =
  | { type: 'START_EDIT_PROMPT'; payload: { sceneId: Id<'reelScenes'>; currentPrompt: string } }
  | { type: 'UPDATE_PROMPT'; payload: { prompt: string } }
  | { type: 'CANCEL_EDIT' }
  | { type: 'START_REGENERATION'; payload: { sceneId: Id<'reelScenes'>; frame: 'first' | 'last' } }
  | { type: 'REGENERATION_COMPLETED' }
  | { type: 'REGENERATION_FAILED'; payload: { error: string } }
  | { type: 'RESET' };

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * Use Scene Image Generation Hook Return Type
 * Main hook for batch image generation
 */
export interface IUseSceneImageGenerationReturn {
  // State
  state: ISceneImageGenerationState;

  // Actions
  generateAllImages: () => Promise<void>;
  reset: () => void;

  // Computed
  isGenerating: boolean;
  canGenerate: boolean;
  hasFailedScenes: boolean;
  progress: ISceneImageGenerationProgress;
}

/**
 * Use Single Scene Image Hook Return Type
 * Hook for individual scene image operations
 */
export interface IUseSingleSceneImageReturn {
  // State
  state: ISingleSceneImageState;

  // Actions
  regenerateImage: (
    sceneId: Id<'reelScenes'>,
    frame: 'first' | 'last',
    prompt?: string
  ) => Promise<void>;
  startEditPrompt: (sceneId: Id<'reelScenes'>, currentPrompt: string) => void;
  updateEditedPrompt: (prompt: string) => void;
  cancelEditPrompt: () => void;
  confirmAndRegenerate: () => Promise<void>;

  // Computed
  isRegenerating: boolean;
  isEditing: boolean;
}

/**
 * Count scenes without generated images (first or last frame)
 * Excludes avatar_only scenes as they use pre-generated AI Avatar images
 */
export function countScenesWithoutImages(
  scenes: Array<{
    sceneType: string;
    editedSceneType?: string;
    generatedImageId?: Id<'_storage'>;
    imageGenerationStatus?: ImageGenerationStatus;
    lastFrameImageId?: Id<'_storage'>;
    lastFrameImageGenerationStatus?: ImageGenerationStatus;
  }>
): number {
  return scenes.filter(scene => {
    // Skip avatar_only scenes - they use pre-generated AI Avatar images
    const effectiveType = scene.editedSceneType ?? scene.sceneType;
    if (effectiveType === 'avatar_only') {
      return false;
    }
    const needsFirstFrame = !scene.generatedImageId || scene.imageGenerationStatus === 'failed';
    const needsLastFrame =
      !scene.lastFrameImageId || scene.lastFrameImageGenerationStatus === 'failed';
    return needsFirstFrame || needsLastFrame;
  }).length;
}
